import React from "react";

// Phase 3D.1-B2 - Visual provenance / simulation badges (frontend-only, no data).
//
// HARD RULE: these badges depend ONLY on explicit, reliable metadata fields.
// They are NEVER inferred from the destination name, organisation name, a URL,
// a KPI value, a Preview mode, a hostname, or the presence of the word "demo"
// in a label. No heuristic fallback.
//
// Explicit signals (mirroring the B1 synthetic contract):
//   * DONNEES DE DEMONSTRATION  <=  is_demo === true  OR  provenance === "synthetic_demo"
//   * SIMULATION                <=  simulation === true
// Historical synthetic observations are is_demo=true / simulation=false -> DEMO badge only.

export const DEMO_DISCLAIMER =
  "Données synthétiques utilisées uniquement pour une démonstration. Aucune affiliation avec une organisation réelle.";
export const SIMULATION_DISCLAIMER =
  "Projection illustrative — ne constitue pas un résultat réellement mesuré.";

// Pure predicates (do NOT mutate their input). Accept either the metadata object
// itself OR a container carrying an explicit `meta` sub-object (e.g. an aggregated
// response). Unwrapping `.meta` when present keeps B2 call-sites (meta={data})
// working without any surface change once B3 attaches `data.meta`.
const _resolveMeta = (x) =>
  x && typeof x === "object" && x.meta && typeof x.meta === "object" ? x.meta : x;

export const isSyntheticDemo = (meta) => {
  const m = _resolveMeta(meta);
  return !!m && (m.is_demo === true || m.provenance === "synthetic_demo");
};

export const isSimulation = (meta) => {
  const m = _resolveMeta(meta);
  return !!m && m.simulation === true;
};

export function DemoBadge({ className = "", ...props }) {
  return (
    <span
      role="note"
      aria-label="Données de démonstration"
      title={DEMO_DISCLAIMER}
      data-testid="demo-badge"
      className={`inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-amber-800 ${className}`}
      {...props}
    >
      Données de démonstration
      <span className="sr-only" data-testid="demo-disclaimer">{DEMO_DISCLAIMER}</span>
    </span>
  );
}

export function SimulationBadge({ className = "", ...props }) {
  return (
    <span
      role="note"
      aria-label="Simulation"
      title={SIMULATION_DISCLAIMER}
      data-testid="simulation-badge"
      className={`inline-flex items-center gap-1 rounded-full border border-violet-300 bg-violet-100 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-violet-800 ${className}`}
      {...props}
    >
      Simulation
      <span className="sr-only" data-testid="simulation-disclaimer">{SIMULATION_DISCLAIMER}</span>
    </span>
  );
}

// Renders the appropriate badge(s) PURELY from explicit metadata. Returns null when
// no explicit signal is present: never assumes the data is real, never fabricates
// provenance, never alters any KPI value.
export default function ProvenanceBadges({ meta, className = "" }) {
  const demo = isSyntheticDemo(meta);
  const sim = isSimulation(meta);
  if (!demo && !sim) return null;
  return (
    <span className={`inline-flex items-center gap-1.5 ${className}`} data-testid="provenance-badges">
      {demo && <DemoBadge />}
      {sim && <SimulationBadge />}
    </span>
  );
}
